import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, Youtube, Facebook } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    solutions: [
      { name: 'Plataforma EAD', path: '/solucoes/plataforma-ead' },
      { name: 'Produção de Cursos', path: '/solucoes/producao-cursos' },
      { name: 'NR-01 Psicossocial', path: '/solucoes/nr01' },
    ],
    company: [
      { name: 'Sobre Nós', path: '/sobre' },
      { name: 'Cases de Sucesso', path: '/cases' },
      { name: 'Blog', path: '/blog' },
      { name: 'Contato', path: '/contato' },
    ],
    resources: [
      { name: 'Central de Ajuda', path: '#' },
      { name: 'Política de Privacidade', path: '#' },
      { name: 'Termos de Uso', path: '#' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: Linkedin, url: '#' },
    { name: 'Instagram', icon: Instagram, url: '#' },
    { name: 'YouTube', icon: Youtube, url: '#' },
    { name: 'Facebook', icon: Facebook, url: '#' },
  ];

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_a8441df1-5f2f-4661-9386-182f70a7a0c4/artifacts/6sz36v20_Logo%20csp%20preto.png"
              alt="CSP Cursos Online"
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-slate-400 mb-6 max-w-sm">
              Transformamos a educação corporativa da sua empresa com plataforma EAD personalizada, 
              produção profissional de cursos e suporte especializado em NR-01.
            </p>
            <div className="space-y-3">
              <a
                href="mailto:contato@cspcursos.com.br"
                className="flex items-center gap-3 text-slate-400 hover:text-orange-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-5 w-5" />
                contato@cspcursos.com.br
              </a>
              <a
                href="tel:+5500000000000"
                className="flex items-center gap-3 text-slate-400 hover:text-orange-400 transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="h-5 w-5" />
                (00) 0000-0000
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>São Paulo, SP - Brasil</span>
              </div>
            </div>
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Soluções
            </h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-slate-400 hover:text-orange-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Empresa
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-slate-400 hover:text-orange-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
              Recursos
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-slate-400 hover:text-orange-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Links */}
            <div className="mt-8">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">
                Redes Sociais
              </h4>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-800 text-slate-400 hover:bg-blue-600 hover:text-white transition-all"
                    aria-label={social.name}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} CSP Cursos Online. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="#"
                className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
              >
                Política de Privacidade
              </Link>
              <Link
                to="#"
                className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
